import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Chip, CircularProgress, IconButton, InputAdornment, Paper, Stack, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import { Print as PrintIcon, Search as SearchIcon } from '@mui/icons-material';
import AlertDialog from '../../../components/AlertDialog';
import useAlert from '../../../hooks/useAlert';
import usePdfPreview from '../../../hooks/usePdfPreview';
import { auditLogsAPI, comptoirAPI, comptoirDocumentsAPI, comptoirServicesAPI } from '../../../services/api';
import { getModuleTableTheme } from '../../../theme/tableTheme';
import { ComptoirDocuments as Docs, ComptoirPaiement } from '../../../types/comptoir';
import { extractErrorMessage } from '../../../utils/errorHandler';
import { COLOR, ETAT_TONE, StateChip, dh, fDate } from '../ui';

type Onglet = 'devis' | 'avances' | 'paiements' | 'livraisons' | 'generaux';
const ONGLETS: [Onglet, string][] = [
  ['devis', 'Devis'], ['avances', "Bons d'avance"], ['paiements', 'Bons de paiement'], ['livraisons', 'Bons de livraison'], ['generaux', 'Bons généraux'],
];
const has = (q: string, ...xs: (string | null | undefined)[]) => !q || xs.some((x) => (x || '').toLowerCase().includes(q));

/** Comptoir › Bons & Documents (prototype V8 « Bons »): every printable document, by kind. */
const ComptoirDocuments: React.FC = () => {
  const [onglet, setOnglet] = useState<Onglet>('devis');
  const [q, setQ] = useState('');
  const { data, isLoading } = useQuery<Docs>({ queryKey: ['comptoir', 'documents'], queryFn: () => comptoirDocumentsAPI.list(), staleTime: 0 });
  const { openPreview, pdfPreviewElement } = usePdfPreview();
  const { alertState, showError, hideAlert } = useAlert();
  const t = q.trim().toLowerCase();

  const rows = useMemo(() => {
    if (!data) return [];
    switch (onglet) {
      case 'devis': return data.devis.filter((d) => has(t, d.reference, d.client, d.travaux, d.service_reference));
      case 'avances': return data.avances.filter((d) => has(t, d.reference, d.client, d.bon));
      case 'paiements': return data.paiements.filter((p) => has(t, p.reference, p.client_nom, p.mode, p.reference_paiement,
        ...p.allocations.map((a) => a.service_reference)));
      case 'livraisons': return data.livraisons.filter((d) => has(t, d.reference, d.client, d.service_reference, d.articles));
      default: return data.generaux.filter((d) => has(t, d.reference, d.client));
    }
  }, [data, onglet, t]);

  const pdfs = () => import('../../../utils/comptoirBonsPdfReactPdf');
  const run = async (fn: () => Promise<void>) => { try { await fn(); } catch (e: any) { showError(extractErrorMessage(e)); } };
  const print = (r: any) => run(async () => {
    if (onglet === 'devis') {
      const o = await comptoirAPI.getOffre(r.offre_id);
      openPreview({ getBlob: async () => (await import('../../../utils/comptoirDevisPdfReactPdf')).getComptoirDevisPDFBlob(o), fileName: `Devis_${o.reference}.pdf` });
      auditLogsAPI.logPdfDownload('ComptoirOffre', o.id, `Aperçu Devis_${o.reference}`);
    } else if (onglet === 'paiements') {
      const p = r as ComptoirPaiement;
      openPreview({ getBlob: async () => (await pdfs()).getBonPaiementPDFBlob(p), fileName: `Bon_${p.reference}.pdf` });
      auditLogsAPI.logPdfDownload('ComptoirPaiement', p.id, `Aperçu bon de paiement - ${p.reference}`);
    } else {
      const sv = await comptoirServicesAPI.get(r.service_id);
      if (onglet === 'livraisons') {
        const lv = sv.livraisons.find((l) => l.id === r.livraison_id);
        if (!lv) throw new Error('Bon de livraison introuvable');
        openPreview({ getBlob: async () => (await pdfs()).getBonLivraisonPDFBlob(sv, lv), fileName: `BL_${lv.reference}.pdf` });
        auditLogsAPI.logPdfDownload('ComptoirLivraison', lv.id, `Aperçu bon de livraison - ${lv.reference}`);
      } else if (onglet === 'avances') {
        openPreview({ getBlob: async () => (await pdfs()).getBonAvancePDFBlob(sv), fileName: `Avance_${sv.reference}.pdf` });
        auditLogsAPI.logPdfDownload('ComptoirService', sv.id, `Aperçu bon d'avance - ${sv.reference}`);
      } else {
        openPreview({ getBlob: async () => (await pdfs()).getBonGeneralPDFBlob(sv), fileName: `Bon_general_${sv.reference}.pdf` });
        auditLogsAPI.logPdfDownload('ComptoirService', sv.id, `Aperçu bon général - ${sv.reference}`);
      }
    }
  });

  const head: Record<Onglet, string[]> = {
    devis: ["N° d'offre", 'Date', 'Client', 'Travaux', 'Total', 'Statut', 'Version'],
    avances: ['Service', 'Client', 'Avance demandée', 'Avance reçue', 'Bon', 'État'],
    paiements: ['N° bon', 'Date', 'Client', 'Services', 'Nature', 'Mode', 'Reçu par', 'Montant'],
    livraisons: ['N° BL', 'Date', 'Service', 'Client', 'Articles livrés', 'Livré par', 'État'],
    generaux: ['Service', 'Client', 'État', 'Total', 'Payé', 'Reste'],
  };
  const mono = { fontFamily: 'monospace', fontWeight: 700, color: COLOR, whiteSpace: 'nowrap' } as const;
  const R = { align: 'right' as const, sx: { whiteSpace: 'nowrap' } };
  const cells = (r: any): React.ReactNode[] => {
    switch (onglet) {
      case 'devis': return [<Box sx={mono}>{r.reference}</Box>, fDate(r.date), r.client || '—', r.travaux, r.total != null ? dh(r.total) : '—',
        <Chip size="small" label={r.statut} color={r.service_reference ? 'success' : 'default'} variant="outlined" />,
        r.version > 1 ? `v${r.version} (anciennes conservées)` : 'v1'];
      case 'avances': return [<Box sx={mono}>{r.reference}</Box>, r.client || '—', dh(r.avance_demandee), dh(r.avance_recue),
        r.bon ? `${r.bon} · ${fDate(r.bon_date)}` : '—',
        <Chip size="small" label={r.recue ? 'Reçue' : 'Non reçue'} color={r.recue ? 'success' : 'warning'} variant="outlined" />];
      case 'paiements': return [<Box sx={mono}>{r.reference}</Box>, fDate(r.date), r.client_nom || '—',
        r.allocations.map((a: any) => a.service_reference).join(', '), Array.from(new Set(r.allocations.map((a: any) => a.type))).join(', '),
        `${r.mode}${r.reference_paiement ? ` · ${r.reference_paiement}` : ''}`, r.user_name || '—', dh(r.montant)];
      case 'livraisons': return [<Box sx={mono}>{r.reference}</Box>, fDate(r.date), r.service_reference, r.client || '—', r.articles, r.livre_par || '—',
        <Chip size="small" label={r.etat} color={r.annule ? 'error' : r.etat === 'Commande complète' ? 'success' : 'info'} variant="outlined" />];
      default: return [<Box sx={mono}>{r.reference}</Box>, r.client || '—', <StateChip small label={r.etat_label} t={ETAT_TONE[r.etat_com]} />,
        dh(r.total), dh(r.paye), dh(r.reste)];
    }
  };
  const right = (i: number) => (onglet === 'devis' && i === 4) || (onglet === 'avances' && (i === 2 || i === 3))
    || (onglet === 'paiements' && i === 7) || (onglet === 'generaux' && i >= 3);
  const count = (o: Onglet) => (data ? (data as any)[o].length : 0);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>Bons & Documents</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Tous les documents commerciaux et financiers, toujours liés à leur client et à leur offre : aperçu, PDF et impression.
      </Typography>
      <Paper sx={{ mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1.5} sx={{ px: 2, pt: 1 }}>
          <Tabs value={onglet} onChange={(_, v) => setOnglet(v)} variant="scrollable" sx={{ flex: 1, '& .Mui-selected': { color: `${COLOR} !important` } }}
            TabIndicatorProps={{ sx: { bgcolor: COLOR } }}>
            {ONGLETS.map(([k, l]) => <Tab key={k} value={k} label={`${l} (${count(k)})`} />)}
          </Tabs>
          <TextField size="small" placeholder="N°, client, service…" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 240 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', pb: { xs: 1, md: 0 } }}>{rows.length} document(s)</Typography>
        </Stack>
      </Paper>
      <TableContainer component={Paper} sx={getModuleTableTheme('comptoir')}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {head[onglet].map((h, i) => <TableCell key={h} align={right(i) ? 'right' : undefined}>{h}</TableCell>)}
              <TableCell align="right" width={56} />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5 }}><CircularProgress sx={{ color: COLOR }} /></TableCell></TableRow>
            ) : !rows.length ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                {onglet === 'paiements' ? 'Aucun bon émis. Chaque encaissement génère son propre bon.' : 'Aucun document.'}
              </TableCell></TableRow>
            ) : rows.map((r: any, idx) => (
              <TableRow key={`${onglet}-${r.id ?? r.livraison_id ?? r.offre_id ?? r.service_id}-${idx}`} hover sx={{ cursor: 'pointer' }} onClick={() => print(r)}>
                {cells(r).map((c, i) => <TableCell key={i} {...(right(i) ? R : {})}>{c}</TableCell>)}
                <TableCell align="right">
                  <Tooltip title="Aperçu / imprimer"><IconButton size="small" sx={{ color: COLOR }}><PrintIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <AlertDialog open={alertState.open} type={alertState.type} title={alertState.title} message={alertState.message}
        onClose={hideAlert} onConfirm={alertState.onConfirm} confirmText={alertState.confirmText}
        cancelText={alertState.cancelText} showCancel={alertState.showCancel} />
      {pdfPreviewElement}
    </Box>
  );
};

export default ComptoirDocuments;
